import React, { createContext, useContext, useRef, useState } from "react";
import Moment from "moment";
import { serverGet, serverPut } from "../../middleware/http";
import { apiConst, CP_INTAKE_STATE_SYNC_ENABLED } from "../constant/constant";
import { ecnSession, getMemberId, getMsg, getSearchParams, getUtcTimeNow, isArray, isEnabled, isTimeLessThan, removeSearchParam, updateSearchParams, decodeUrlParam } from "../utils/validation";
import ProgressContext from "../utils/progress";
import { useProfile } from "../utils/profileContext";
import { useNavigate } from "react-router-dom";
import { useAccountSettings } from "./AccSettingContext";
import ConfirmModal from "../../components/common/ConfirmModal";

const AppointmentContext = createContext(null);

export const useAppointment = () => {
    return useContext(AppointmentContext);
};

export const AppointmentProvider = ({ children }) => {
    const { startLoading, finishLoading } = useContext(ProgressContext);
    const { checkUserProfile } = useProfile();
    const { getAccountsettingValue, accountSettings, getAccountSetting } = useAccountSettings();
    const [showNoProviderModal, setShowNoProviderModal] = useState(false);
    const noProviderResolveRef = useRef(null);

    const navigate = useNavigate();

    // Shows the "no provider available within the hour" confirm popup and resolves once the
    // patient picks Continue (true) or Cancel (false). Mounted once here (AppointmentProvider
    // wraps the whole app) so any re-entry point that calls validateUserTimeSlot — post-login,
    // post-OTP, family-member selection — can offer the same popup Booking.js shows.
    const confirmNoProviderFallback = () => {
        return new Promise((resolve) => {
            noProviderResolveRef.current = resolve;
            setShowNoProviderModal(true);
        });
    };

    const handleNoProviderContinue = () => {
        setShowNoProviderModal(false);
        noProviderResolveRef.current?.(true);
    };

    const handleNoProviderCancel = () => {
        setShowNoProviderModal(false);
        noProviderResolveRef.current?.(false);
    };

    // Broader specialty/residing-state/language match, ignoring immediate availability — used
    // only to decide whether the "continue without an immediately available provider" popup
    // should be offered at all. Mirrors Booking.js's checkBroaderProviderMatch. canRedirect=false
    // so a failure here never bounces the patient to the login page mid-decision.
    const checkBroaderProviderMatch = async (specialityId, residingId, patientMemberId) => {
        const patientData = patientMemberId
            ? encodeURIComponent(JSON.stringify([{ MemberId: patientMemberId, ResidingStateId: residingId ? parseInt(residingId) : null, StatusId: 1 }]))
            : "";
        const scheduleOn = getUtcTimeNow();
        const res = await serverGet(
            `${apiConst.getProviderList}?spid=${specialityId}&kwd=-&sdt=${scheduleOn}${residingId ? `&rsid=${residingId}` : ""}${patientMemberId ? `&patientMemberId=${patientMemberId}` : ""}${patientData ? `&patientdata=${patientData}` : ""}`,
            false
        );
        return res?.status == 1 && isArray(res?.data) && res?.data?.length > 0;
    };

    const isUrgentCare247Enabled = async () => isEnabled(await getAccountSetting(), "U247", 1);

    // Two-tier Consult Now without Provider check (mirrors Booking.js's getAvailableProvider):
    // if the immediate-window check already succeeded, proceed. Otherwise check whether ANY
    // provider matches specialty/residing state/language at all; if so, ask the patient to
    // confirm continuing without one immediately available. If neither check finds anything,
    // there's truly no eligible provider.
    const resolveConsultNowWithoutProvider = async (isImmediatelyAvailable, specialityId, residingId, patientMemberId, skipConfirmPrompt = false) => {
        if (isImmediatelyAvailable) return "proceed";

        const hasBroaderMatch = await checkBroaderProviderMatch(specialityId, residingId, patientMemberId);
        if (!hasBroaderMatch) return "blocked";

        // Some entry points (e.g. pediatric-only family member selection) want to proceed
        // without a provider immediately available rather than interrupting with the confirm
        // popup — skipConfirmPrompt lets that caller opt out of the prompt entirely.
        if (skipConfirmPrompt || (await isUrgentCare247Enabled())) return "proceed";

        // Both callers hold a loader (startLoading) for the whole check. The loader overlay sits
        // above Bootstrap modals and blocks clicks, so release it while the popup waits for the
        // patient and take it back afterwards — the caller's finally still balances it.
        finishLoading();
        let proceedWithoutProvider;
        try {
            proceedWithoutProvider = await confirmNoProviderFallback();
        } finally {
            startLoading();
        }
        return proceedWithoutProvider ? "proceed" : "cancelled";
    };

    // Consult Now without Provider: some accounts skip Booking.js (COM) entirely when only one
    // encounter mode is configured — Service.js's COM auto-skip branch jumps straight from APM
    // to REC, so Booking.js's provider-availability check + popup never runs. Reason.js calls
    // this on mount to run the same check itself in that case. A no-op ("proceed") for every
    // other entry: non-NWP methodIds, or an attempt that already went through Booking.js
    // (nwpok is already set).
    const checkConsultNowOnRecEntry = async () => {
        try {
            startLoading();
            const brtype = getSearchParams("brtype");
            if (brtype !== "GT6") return "proceed";
            if (getSearchParams("nwpok")) return "proceed";

            const specialityId = getSearchParams("spid");
            const residingId = getSearchParams("rsid");
            const durationId = getSearchParams("did");
            const familyMemberId = getSearchParams("fmid");
            const scheduleOn = getUtcTimeNow();

            let baseUrl = `?did=${durationId}&sid=${specialityId}&mid=3&sdt=${scheduleOn}`;
            if (residingId) baseUrl += `&rid=${residingId}`;
            if (familyMemberId) baseUrl += `&pmid=${familyMemberId}`;
            // The booking being re-validated, so the slot checks can tell this appointment's own
            // reservation apart from someone else's and stop it colliding with itself.
            if (getSearchParams("eid")) baseUrl += `&eid=${getSearchParams("eid")}`;

            const res = await serverGet(`${apiConst.validateUserTimeSlot}${baseUrl}`);
            const isImmediatelyAvailable = res?.status == 1 && res?.data == "100";

            const patientMemberId = familyMemberId || (await getMemberId());
            const outcome = await resolveConsultNowWithoutProvider(isImmediatelyAvailable, specialityId, residingId, patientMemberId);

            if (outcome === "proceed") {
                updateSearchParams("nwpok", "1");
            }

            return outcome;
        } catch (e) {
            console.error(e);
        } finally {
            // progParams(false);
            finishLoading();
        }
    };

    const validateUserTimeSlot = async (path, reLoad, handleFlow, steps, skipNoProviderPrompt = false) => {
        try {
            startLoading();
            const url = new URL(path, window.location.origin);
            const _memberId = url.searchParams.has("memberid") ? url.searchParams.get("memberid") : null;
            const bookingDate = url.searchParams.has("ad") ? url.searchParams.get("ad") : null;
            let _scheduleOn = url.searchParams.has("ts") ? url.searchParams.get("ts") : null;
            const _durationId = url.searchParams.has("did") ? url.searchParams.get("did") : null;
            const _residingId = url.searchParams.has("rsid") ? url.searchParams.get("rsid") : null;
            const _specialityId = url.searchParams.has("spid") ? url.searchParams.get("spid") : null;
            let _methodId = url.searchParams.has("brtype") ? url.searchParams.get("brtype") : null;
            const _familyMemberId = url.searchParams.has("fmid") ? url.searchParams.get("fmid") : null;
            const _scheduleId = url.searchParams.has("schid") ? url.searchParams.get("schid") : null;
            const _encounterId = url.searchParams.has("eid") ? url.searchParams.get("eid") : null;
            _methodId = _methodId == "RW8" ? 2 : _methodId == "GT6" ? 3 : _methodId == "SD3" ? 1 : _methodId == "UMS" ? 4 : "1";

            let startOfDay = new Date(bookingDate);
            startOfDay.setHours(0, 0, 0, 0);

            let endOfDay = new Date(bookingDate);
            endOfDay.setHours(23, 59, 59, 999);

            let startOfDayUtc = Moment.utc(startOfDay).format("YYYY-MM-DD hh:mm:ss A");
            let endOfDayUtc = Moment.utc(endOfDay).format("YYYY-MM-DD hh:mm:ss A");

            if (_methodId == "3" || _methodId == "4") _scheduleOn = getUtcTimeNow();

            let baseUrl = `?did=${_durationId}&sid=${_specialityId}&mid=${_methodId}&sdt=${_scheduleOn}`;

            if (_methodId != "3" && _methodId != "4") {
                baseUrl += `&fdt=${startOfDayUtc}&tdt=${endOfDayUtc}`;
            }

            if (_residingId) {
                baseUrl += `&rid=${_residingId}`;
            }
            if ((_methodId == "1" || _methodId == "4") && _memberId) {
                baseUrl += `&memberId=${_memberId}`;
            }

            if (_familyMemberId) {
                baseUrl += `&pmid=${_familyMemberId}`;
            }

            // Read off the path being validated, like every other value above -- this also runs from
            // re-entry points (post-login, OTP) where that path is the booking and window.location is
            // still the page that triggered the check.
            if (_encounterId) {
                baseUrl += `&eid=${_encounterId}`;
            }

            const customNavigate = (value, _path, _err, id) => {
                if (reLoad) {
                    window.location.href = value;
                } else {
                    updateSearchParams("current", _path ? _path : "REC");
                    if (_path) {
                        updateSearchParams("tsver", _err ? _err : "");
                        // fmid (the selected family member) is never persisted anywhere except this
                        // URL param — every downstream consumer (Reason.js's submit, ITM, forms,
                        // pharmacy, insurance, payment, summary, ...) reads it via getSearchParams
                        // only. It must survive this bounce back to COM: Service.js's COM/REC
                        // handling already treats FAMILYMEMBER as "completed" once it's in
                        // serviceFlowHistory and deliberately skips re-showing it on re-entry (see the
                        // comment on the COM branch's prune guard) — stripping fmid here silently
                        // defaulted the booking to the logged-in parent instead of the chosen child
                        // once a new slot was picked and Proceed was clicked again.
                        removeSearchParam("ad");
                        removeSearchParam("ts");
                    }
                    if (id) {
                        updateSearchParams("memberid", id);
                    }
                    if (handleFlow) handleFlow(_path ? _path : "REC", steps);
                }
            };

            const clearParams = (url, resCode) => {
                url.searchParams.delete("ts");
                url.searchParams.delete("ad");
                // fmid must survive this bounce back to COM — see the matching comment in
                // customNavigate above.
                url.searchParams.append("tsver", resCode); // TIME SLOT VERIFICATION

                if (url.searchParams.has("current")) {
                    url.searchParams.set("current", "COM");
                }

                return customNavigate(url.toString(), "COM", resCode);
            };

            const res = await serverGet(`${apiConst.validateUserTimeSlot}${baseUrl}`);
            const isImmediatelyAvailable = res?.status == 1 && res?.data == "100";

            // Booking.js already asked (or didn't need to ask) this exact question before
            // sending the patient to REC — the "nwpok" marker rides along through the mandatory
            // REC-requires-login redirect (it's part of the returnUrl). Re-validating here would
            // ask the same question again while the patient is stuck looking at the login page.
            // Deliberately NOT consumed/stripped here: this checkpoint can fire more than once in
            // a row for the same booking attempt (post-login, then again post-family-member-
            // selection, etc.), and updateSearchParams/history keep it in the URL across those
            // client-side step transitions regardless — removing it after the first hit left the
            // later ones unprotected and re-asking. It only stops applying once the patient
            // navigates to a genuinely new booking attempt (a fresh URL that never had it).
            const _nwpAlreadyConfirmed = url.searchParams.has("nwpok");

            // Consult Now without Provider re-entry (post-login, post-OTP, family-member
            // selection): mirrors Booking.js's two-tier check — immediate availability, else a
            // broader specialty/residing-state/language match with a confirm popup, else block.
            const handleConsultNowWithoutProvider = async () => {
                if (_nwpAlreadyConfirmed) {
                    return customNavigate(path, "", "", _memberId);
                }

                const patientMemberId = _familyMemberId || (await getMemberId());
                const outcome = await resolveConsultNowWithoutProvider(isImmediatelyAvailable, _specialityId, _residingId, patientMemberId, skipNoProviderPrompt);

                if (outcome === "proceed") {
                    return customNavigate(path, "", "", _memberId);
                } else if (outcome === "blocked") {
                    return clearParams(url, "232");
                } else {
                    // "cancelled" — patient declined; send them back to COM to pick a different
                    // consultation mode/specialty rather than leaving the page stuck.
                    return clearParams(url, "");
                }
            };

            if (_scheduleId && window.location.pathname.includes("/security")) {
                const _canNavigate = await canNavigateToReason(_scheduleOn);

                if (_canNavigate) {
                    return customNavigate(path, "", "", _memberId);
                } else if (_methodId == "3") {
                    return handleConsultNowWithoutProvider();
                } else {
                    clearParams(url, "232");
                }
            } else if (_methodId == "3") {
                return handleConsultNowWithoutProvider();
            } else {
                if (res?.status == 1 && res?.data == "100") {
                    //NAVIGATE TO REASON PAGE
                    if (_methodId == 1 || _methodId == 4) {
                        return customNavigate(path, "", "", _memberId);
                    } else {
                        const _res = await serverGet(`${apiConst.getAvailableProvider}${baseUrl}`);

                        if (_res?.data && _res?.status == 1) {
                            const memberProviders = JSON.parse(_res?.data);

                            if (memberProviders?.[0]?.ProviderId) {
                                if (url.searchParams.has("memberid")) {
                                    url.searchParams.set("memberid", memberProviders[0]?.ProviderId);
                                }
                                return customNavigate(path, "", "", memberProviders[0]?.ProviderId);
                            }
                        } else {
                            clearParams(url, "232");
                        }
                    }
                } else {
                    //NAVIGATE BACK TO BOOKING PAGE
                    const _errCodes = ["418", "419"];

                    if (_errCodes.includes(res?.data?.toString())) {
                        if (!window.location.pathname.includes("/security")) window.alert(getMsg(res?.data));
                        if (window.location.pathname.includes("/security")) return clearParams(url, res?.data);

                        if (reLoad) {
                            window.location.href = "/dashboard";
                        } else {
                            return navigate("/dashboard");
                        }
                    } else {
                        clearParams(url, res?.data);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            // progParams(false);
            finishLoading();
        }
    };


    const getProductCodes = async () => {
        try {
            return decodeUrlParam(getSearchParams("items")) || [];
        } catch (e) {
            console.error("Error in getProductCodes", e);
            return [];
        }
    };

    // skipNwpRecheck: the caller (e.g. PendingAppointment.js's canNavigateToReason) already ran
    // the Consult Now without Provider availability check + confirm popup itself before deciding
    // to resume this pending request — mark the resulting REC URL with "nwpok" so Reason.js's
    // own checkConsultNowOnRecEntry doesn't ask the same question a second time.
    // `patient` is accepted for call-site compatibility (several callers already have it in hand)
    // but is deliberately not consulted for residing state below -- see the rsid comment.
    const pendingAppointment = async (encounter, patient, redirect, isSkip, extraParams = null, startStep = "REC", skipNwpRecheck = false) => {
        try {
            // progParams(true);
            startLoading();

            let _isProfile = null;

            if (!isSkip) {
                _isProfile = await checkUserProfile();
            }

            let _isIdverification = false;

            if (encounter?.serviceId && !isSkip) {
                const _res = await serverGet(`${apiConst.serviceWorkFlowSteps}?serviceid=${encounter?.serviceId}&code=IIN`);

                if (_res?.status == 1 && _res?.data) {
                    _isIdverification = _res?.data?.find((l) => l.workFlowStepCode == "IIN" && l.code == "1");
                }
            }

            if (!_isProfile?.pofileCompleted || (_isIdverification && !_isProfile?.idVerification) || isSkip || encounter?.statusId == 5) {
                const _memberId = await getMemberId();

                const _code = encounter.methodId == 3 ? "GT6" : encounter.methodId == 2 ? "RW8" : encounter.methodId == 4 ? "UMS" : "SD3";

                const _scheduleOn =
                    encounter.methodId == 3 || encounter.methodId == 4 ? getUtcTimeNow() : encounter.scheduleOn;

                // startStep === "COM" resumes at the time-slot page (used when a scheduled appointment's
                // time has already passed): drop the stale scheduleOn so the user picks a fresh slot, and
                // for RW8 (centralized) drop the fixed provider so one is reassigned for the new slot.
                // Mirrors checkPassedTime's COM redirect. All other steps keep the existing scheduleOn/provider.
                const _resumeAtTimeSlot = startStep === "COM";

                let _url = `/service?current=${startStep}&did=${encounter.durationId}&emid=${encounter.modeId}&spid=${encounter.specialityId}&rnote=${encounter.note}&rid=${encounter.reasonId}&r=${encounter.reason}&eid=${encounter.id}&schid=${encounter.scheduleId}&brtype=${_code}`;

                if (!(_resumeAtTimeSlot && _code === "RW8")) {
                    _url += `&memberid=${encounter.providerId}`;
                }

                if (!_resumeAtTimeSlot) {
                    _url += `&ts=${_scheduleOn}`;
                }

                // Bind the previously selected consultation mode (m = mode code) and duration
                // (dn = duration name) so the time-slot page preselects them — Booking.js
                // restores the selection from m/dn, not from the emid/did ids (did alone is
                // only honored for src=ext). Without these, re-entering the time-slot page
                // (e.g. passed-time resume via checkPassedTime) resets both to the defaults.
                if (encounter?.encounterModeCode) {
                    _url += `&m=${encounter.encounterModeCode}`;
                }

                if (encounter?.duration) {
                    _url += `&dn=${encodeURIComponent(encounter.duration)}`;
                }

                // The encounter's OWN residing state only -- it is already correctly null for a
                // service whose PLR step (and/or the account's SCL setting) wasn't enabled at
                // creation time (schedule/create only writes what the client sent), so falling back
                // to the patient's on-file value here would resurrect exactly the leak that value
                // represents for a resumed booking whose service doesn't ask for one.
                if (encounter?.residingStateId) {
                    _url += `&rsid=${encounter.residingStateId}`;
                }

                // Phone/Phone-Online modes capture a schedule phone number (snum) on the mode-selection page
                // and persist it on the encounter (PhoneNo). When resuming a pending appointment the
                // mode-selection page is skipped, so re-bind snum from the encounter to keep it in the URL
                // (shown in DoctorSummary) and re-saved by the Reason page. encodeURIComponent preserves
                // the leading "+" and space in "diallingCode phone" across the URL round-trip.
                if (encounter?.phoneNo) {
                    _url += `&snum=${encodeURIComponent(encounter.phoneNo)}`;
                }

                if (encounter?.serviceOptionId) {
                    _url += `&speicalityservice=${encounter?.serviceOptionId}`;
                }

                if (encounter?.serviceId) {
                    _url += `&serviceid=${encounter?.serviceId}`;
                }

                if (encounter.consultationTypeId == 2 && encounter.parentId) {
                    _url += `&ftypeid=${encounter.consultationTypeId}`;
                    _url += `&parent=${encounter.parentId}`;
                }

                if (_memberId && _memberId?.toLowerCase() != encounter.memberId) {
                    _url += `&fmid=${encounter.memberId}`;
                }

                if (skipNwpRecheck) {
                    _url += "&nwpok=1";
                }

                // Bind caller-supplied extras (e.g. paytypecode from the encounter's stored
                // payment type, labrxId) into the resume URL — every call site already builds
                // this object expecting it to land here, so this must stay generic rather than
                // hardcoding individual keys.
                if (extraParams && typeof extraParams === "object") {
                    Object.entries(extraParams).forEach(([key, value]) => {
                        if (value !== null && value !== undefined && value !== "") {
                            _url += `&${key}=${encodeURIComponent(value)}`;
                        }
                    });
                }

                // The REC step re-fetches this same encounter for exactly one reason — to confirm it
                // is still pending — and the record that answers it is the one in hand here, read
                // from the server moments ago on the way in. Every caller reaches this line with a
                // fresh `encounter/getencounter` response, so the confirmation is recorded rather
                // than bought a second time. Only a genuinely pending record is recorded, which is
                // the same verdict REC would reach; anything else leaves nothing behind and REC
                // asks for itself. REC consumes this once, so a reload or any later re-entry
                // re-verifies against the server.
                if (encounter?.id && encounter?.statusId == 5) {
                    ecnSession(encounter.id, "resumeVerifiedEncounterId");
                }

                if (redirect) {
                    window.location.href = _url;
                    return true;
                } else {
                    navigate(`${_url}`);
                    return true;
                }
            }
            return false;
        } catch (e) {
            console.error(e);
        } finally {
            // progParams(false);
            finishLoading();
        }
    };

    const popFromNavigationHistory = () => {
        const sessionStore = ecnSession();
        const renderedModules = isArray(sessionStore?.serviceFlowHistory) ? sessionStore?.serviceFlowHistory : [];

        if (renderedModules.length === 0) return null;

        const previousItem = renderedModules[renderedModules.length - 2];
        const newHistory = renderedModules.slice(0, -1);

        ecnSession(newHistory, "serviceFlowHistory");

        return previousItem;
    };

    // Reconciles the Pending appointment with the current selections on a Reason re-submit, for the
    // fields schedule/update cannot write. No-ops without a schid; never throws, so the submit that
    // follows proceeds regardless. Field resolution mirrors Reason.js's own formadata.
    const syncIntakeStateIfNeeded = async () => {
        if (!CP_INTAKE_STATE_SYNC_ENABLED) return;

        const scheduleId = getSearchParams("schid");
        if (!scheduleId) return;

        try {
            let _methodId = getSearchParams("brtype");
            _methodId = _methodId == "RW8" ? 2 : _methodId == "GT6" ? 3 : _methodId == "SD3" ? 1 : _methodId == "UMS" ? 4 : "1";

            const body = {
                PatientId: null,
                // fmid only exists for a family-member booking, so a self-booking falls back to the
                // account holder -- without it the API resolves no patient and rejects the call.
                MemberId: getSearchParams("fmid") ?? (await getMemberId()),
                MethodId: _methodId,
                SpecialityId: getSearchParams("spid"),
                ServiceId: getSearchParams("serviceid") || ecnSession()?.workFlowServiceId || null,
            };

            const res = await serverPut(`${apiConst.syncintake}${scheduleId}`, body);

            if (res?.status != 1) {
                // On a failure the BFF returns { StatusCode, Result, ... } with Result a JSON string.
                let _messageCode = res?.messageCode?.[0];
                if (!_messageCode) {
                    try {
                        _messageCode = JSON.parse(res?.data?.Result ?? "{}")?.MessageCode;
                    } catch (parseErr) {
                        // ignore -- logging only
                    }
                }
                console.error("syncIntakeStateIfNeeded: schedule/syncintake did not succeed", _messageCode ?? res);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const canNavigateToReason = async (encounterOn) => {
        const appointmentPriorDuration = getAccountsettingValue(accountSettings, "ADN");
        const hasSnpEnabled = isEnabled(accountSettings, "SNP", 1) && parseInt(appointmentPriorDuration) > 0;

        if (hasSnpEnabled) {
            return isTimeLessThan(encounterOn, appointmentPriorDuration);
        }

        return isTimeLessThan(encounterOn);
    };

    return (
        <AppointmentContext.Provider
            value={{
                validateUserTimeSlot,
                popFromNavigationHistory,
                pendingAppointment,
                getProductCodes,
                checkConsultNowOnRecEntry,
                syncIntakeStateIfNeeded,
            }}
        >
            {children}
            <ConfirmModal
                show={showNoProviderModal}
                messageKey="cnwnelig"
                onContinue={handleNoProviderContinue}
                onCancel={handleNoProviderCancel}
            />
        </AppointmentContext.Provider>
    );
};
